import * as Yup from 'yup';
import { Base64File } from '@synergy/models';
import { Box, FormHelperText, Grid } from '@mui/material';
import { ErrorCode, FileRejection } from 'react-dropzone';
import { Field, FieldProps, Formik, FormikProps, useFormikContext } from 'formik';
import { FormattedMessage } from 'react-intl';
import {
    FormikPoolDatePicker,
    PoolFormattedMessage,
    PoolMarkdownEditor,
    PoolSpinner,
    PoolTextField,
    PoolTypography,
    ScrollToFieldError,
} from '@synergy/components';
import { ImageMimeType, ImageRatio, ImageType } from '@synergy/models';
import { MAX_FILE_SIZE } from '@synergy/config';
import { PaloFormSection } from '@synergy/components-palo';
import { ReactElement } from 'react';
import { base64encode, getLocalizedString } from '@synergy/utils';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';

import * as NewsPostActions from '../store/news-post-actions';
import * as NewsPostDetailActions from './store/news-post-detail-actions';
import { NewsPostDetailFormValues } from './models/news-post-detail-form-values';
import { PaloContentDetailShell } from '../../palo/palo-content-detail-shell';
import { PictureDropzone } from '../../components/picture-dropzone';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import {
    newsPostDetailFormValues,
    newsPostDetailRequestingSelector,
    newsPostDetailSelector,
} from './store/news-post-detail-selector';
import { selectedLanguageSelector } from '../../../../common/selectors/selected-language-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const validImageTypes = [ImageType.JPEG, ImageType.PNG, ImageType.WebP];
const validImageMimeTypes = validImageTypes.map(type => ImageMimeType[type].toString());

export const NewsPostDetail = () => {
    const requesting = useAppSelector(newsPostDetailRequestingSelector);
    const selectedLanguage = useAppSelector(selectedLanguageSelector);
    const formValues = useAppSelector(newsPostDetailFormValues);
    const newsPost = useAppSelector(newsPostDetailSelector);
    const currentLocale = useAppSelector(state => state.currentLocale);
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const { id: newsPostIdParam } = useParams<{ id: string }>();

    useEffect(() => {
        if (newsPostIdParam && !isNaN(+newsPostIdParam)) {
            dispatch(NewsPostDetailActions.fetchNewsPostDetail(+newsPostIdParam));
        }

        return () => {
            dispatch(NewsPostDetailActions.resetNewsPostDetail());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isNaN(Number(newsPostIdParam)) && newsPost?.id) {
            navigate(ROUTES.contentManagement.poolNews.detail.replace(':id', newsPost.id.toString()));
        }
        // ignore newsPostIdParam, should only rerun if newsPost.id changes when new newsPost is created
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, newsPost?.id]);

    const handleSubmit = async (values: NewsPostDetailFormValues) => {
        dispatch(NewsPostDetailActions.saveNewsPostDetail(values));
    };

    const handleDelete = () => {
        if (newsPostIdParam && !isNaN(+newsPostIdParam)) {
            dispatch(NewsPostActions.deleteNewsPost(+newsPostIdParam));
        }
    };

    const isNew = newsPostIdParam ? isNaN(+newsPostIdParam) : false;

    const validationSchema = Yup.object().shape({
        image: Yup.mixed().required('app.error.required'),
        publishAt: Yup.date().required().typeError('app.error.required'),
        title: Yup.object().shape({
            [selectedLanguage]: Yup.string().required('app.error.required'),
        }),
        text: Yup.object().shape({
            [selectedLanguage]: Yup.string().required('app.error.required'),
        }),
    });

    const handleDrop =
        (form: FormikProps<NewsPostDetailFormValues>, name: string) =>
        async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
            if (rejectedFiles.length > 0) {
                if (rejectedFiles[0].errors[0].code === ErrorCode.FileInvalidType) {
                    form.setFieldError(name, 'app.error.invalidImageType');
                } else if (rejectedFiles[0].errors[0].code === ErrorCode.FileTooLarge) {
                    form.setFieldError(name, 'app.error.maxFiveMb');
                } else {
                    form.setFieldError(name, 'app.error.maxOneFile');
                }

                form.setFieldTouched(name, true, false);
                return;
            }

            if (!acceptedFiles[0]?.type || !validImageMimeTypes.includes(acceptedFiles[0].type)) {
                form.setFieldError(name, 'app.error.invalidImageType');
                form.setFieldTouched(name, true, false);
                return;
            }

            const image = { name: acceptedFiles[0].name, contentBase64: await base64encode(acceptedFiles[0]) };
            form.setFieldValue(name, image);
        };

    return (
        <PoolSpinner enabled={requesting} sx={{ width: '100%', height: '100%' }}>
            <Formik
                initialValues={formValues}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
                enableReinitialize={true}
            >
                <PaloContentDetailShell
                    title="app.contentManagement.newsPost.newsPostHeader"
                    isNew={isNew}
                    onDelete={handleDelete}
                    listRoute={ROUTES.contentManagement.poolNews.list}
                    deleteTitle={<FormattedMessage id="app.contentManagement.newsPost.delete" />}
                    deleteWarning={<NewsPostDeleteWarning />}
                >
                    <ScrollToFieldError />

                    <Box m={theme => theme.spacing(2, theme.general.pageContentSpacing)}>
                        <PaloFormSection title={<FormattedMessage id="app.contentManagement.newsPost.texts" />}>
                            <Grid container={true} spacing={2}>
                                <Grid item={true} xs={12}>
                                    <FormattedMessage id="app.contentManagement.newsPost.textParagraph" />
                                </Grid>
                                <Grid item={true} xs={12}>
                                    <Field name={`title.${selectedLanguage}`}>
                                        {({ field, form, meta }: FieldProps<string>) => (
                                            <PoolTextField
                                                {...field}
                                                fullWidth={true}
                                                label={
                                                    <FormattedMessage id="app.contentManagement.newsPost.titleLabel" />
                                                }
                                                value={field.value}
                                                onChange={e =>
                                                    form.setFieldValue(
                                                        field.name,
                                                        (e.target.value?.length ?? 0) > 120
                                                            ? field.value
                                                            : e.target.value
                                                    )
                                                }
                                                error={!!meta.touched && !!meta.error}
                                                required={true}
                                                helperText={
                                                    meta.touched && meta.error ? (
                                                        <FormattedMessage id={meta.error} />
                                                    ) : (
                                                        <FormattedMessage
                                                            id="app.contentManagement.newsPost.titleCharacterCount"
                                                            values={{ value: field.value?.length ?? 0 }}
                                                        />
                                                    )
                                                }
                                            />
                                        )}
                                    </Field>
                                </Grid>
                                <Grid item={true} xs={12}>
                                    <Field name={`text.${selectedLanguage}`}>
                                        {({ field, form, meta }: FieldProps<string>) => (
                                            <>
                                                <PoolMarkdownEditor
                                                    {...field}
                                                    value={field.value}
                                                    onChange={value =>
                                                        form.setFieldValue(`text.${selectedLanguage}`, value)
                                                    }
                                                    error={!!meta.touched && !!meta.error}
                                                />
                                                {meta.touched && meta.error ? (
                                                    <FormHelperText error={true} sx={{ ml: 1.75 }}>
                                                        <FormattedMessage id={meta.error} />
                                                    </FormHelperText>
                                                ) : null}
                                            </>
                                        )}
                                    </Field>
                                </Grid>
                            </Grid>
                        </PaloFormSection>

                        <PaloFormSection title={<FormattedMessage id="app.contentManagement.picture" />}>
                            <Grid container={true} spacing={2}>
                                <Grid item={true} xs={6}>
                                    <Field name="image">
                                        {({ field, form, meta }: FieldProps<Base64File>) => (
                                            <>
                                                <PictureDropzone
                                                    onDrop={handleDrop(form, field.name)}
                                                    error={!!meta.touched && !!meta.error}
                                                    maxSize={MAX_FILE_SIZE}
                                                    imageRatio={ImageRatio.SixteenByNine}
                                                    path={field.value?.contentBase64 ?? field.value?.pathname}
                                                    disabled={selectedLanguage !== currentLocale}
                                                >
                                                    <PoolTypography
                                                        typography="body2"
                                                        component="span"
                                                        color="grey.700"
                                                        id="app.contentManagement.picture.desktopMobile"
                                                        values={{ validImageTypes: validImageTypes.join(', ') }}
                                                    />
                                                    <PoolTypography
                                                        typography="body2"
                                                        component="span"
                                                        color="grey.700"
                                                        id="app.contentManagement.picture.constraint"
                                                        values={{ resolution: '700x394px (16:9)' }}
                                                    />
                                                </PictureDropzone>
                                                {!!meta.error && !!meta.touched && (
                                                    <FormHelperText error={true} sx={{ ml: 1.75 }}>
                                                        <FormattedMessage
                                                            id={meta.error}
                                                            values={{ validImageTypes: validImageTypes.join(', ') }}
                                                        />
                                                    </FormHelperText>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </Grid>
                            </Grid>
                        </PaloFormSection>

                        <PaloFormSection title={<FormattedMessage id="app.contentManagement.newsPost.advertisement" />}>
                            <Grid container={true} spacing={2}>
                                <Grid item={true} xs={6}>
                                    <FormikPoolDatePicker
                                        name="publishAt"
                                        label={<FormattedMessage id="app.contentManagement.newsPost.publishAt" />}
                                        required={true}
                                        readOnly={selectedLanguage !== currentLocale}
                                    />
                                </Grid>
                            </Grid>
                        </PaloFormSection>
                    </Box>
                </PaloContentDetailShell>
            </Formik>
        </PoolSpinner>
    );
};

/** The warning names the entry, which only the form context knows. */
const NewsPostDeleteWarning = (): ReactElement => {
    const { supportedLocales } = useAppSelector(state => state);
    const { values } = useFormikContext<NewsPostDetailFormValues>();

    return (
        <PoolFormattedMessage
            id="app.contentManagement.newsPost.deleteWarning"
            values={{ name: getLocalizedString(supportedLocales, values.title) }}
        />
    );
};
